import { useMemo } from "react";
import { z } from "zod";

import RadarChartView from "@/components/ui/radar-chart/RadarChartView";
import { buildRadarChartCatalogModel } from "@/components/ui/radar-chart/radarChartUtils";
import { chartHexColorSchema } from "@/lib/utils/chartUtils";

const radarChartPropsSchema = z
  .object({
    title: z.string().nullable().optional(),
    data: z.array(z.record(z.string(), z.unknown())),
    /** Axis field: each row is one spoke (e.g. dimension). */
    nameKey: z.string(),
    /** Single series: numeric column in `data`. Omit when using `dataKeys`. */
    dataKey: z.string().nullable().optional(),
    /** Optional single-series colour. */
    color: chartHexColorSchema.nullable().optional(),
    /** Multiple overlaid series (e.g. one per feed). Omit when using `dataKey`. */
    dataKeys: z
      .array(
        z.object({
          key: z.string(),
          label: z.string(),
          color: chartHexColorSchema.nullable().optional(),
        }),
      )
      .nullable()
      .optional(),
    height: z.number().nullable().optional(),
  })
  .superRefine((val, ctx) => {
    const hasMulti = (val.dataKeys?.length ?? 0) > 0;
    const hasSingle = val.dataKey != null && val.dataKey !== "";
    if (hasSingle === hasMulti) {
      ctx.addIssue({
        code: "custom",
        message:
          "Provide exactly one of: dataKey (single series) or dataKeys (one or more series).",
      });
    }
  });

export const ChatRadarChartSpec = {
  props: radarChartPropsSchema,
  description:
    'Radar/spider chart for multi-dimensional comparison. Each row becomes one axis (spoke). Props: title (string or null), data (array of records), nameKey (axis label field — each distinct value is one spoke). For one series pass dataKey (numeric column) and optional color (#RGB or #RRGGBB). For two or more overlaid series pass dataKeys: [{ key, label, color? }, ...], with optional #RGB or #RRGGBB per series; each row must include a numeric value per series key on the same scale. For readable overlays, ensure series differ enough per dimension. Series keys named positive, negative, or neutral (case-insensitive) get semantic sentiment colors; others use var(--chart-1) through var(--chart-5) by index when color is omitted. Legend appears when there are two or more series. Height optional (px). Bind read-only tool data with { "$state": "/path" } on props when needed.',
  example: {
    title: "Coverage profile",
    nameKey: "dimension",
    dataKey: "score",
    data: [
      { dimension: "Volume", score: 72 },
      { dimension: "Impact", score: 68 },
      { dimension: "Engagement", score: 81 },
    ],
    height: 280,
  },
} as const;

/** Parses props and renders {@link RadarChartView}. */
export const ChatRadarChartFromProps = ({
  props,
}: {
  props: Record<string, unknown>;
}) => {
  const resolved = useMemo(() => {
    const parsed = radarChartPropsSchema.safeParse(props);
    if (!parsed.success) return null;
    const { data, nameKey, dataKey, dataKeys, color, title, height } =
      parsed.data;
    const chart = buildRadarChartCatalogModel({
      data,
      nameKey,
      dataKey: dataKey ?? null,
      dataKeys: dataKeys ?? null,
      color: color ?? null,
    });
    return { title, height, chart };
  }, [props]);

  if (!resolved) {
    return null;
  }

  const { title, height, chart } = resolved;

  if (chart.mode === "multi") {
    return (
      <RadarChartView
        title={title ?? null}
        height={height ?? null}
        data={chart.chartData}
        chartConfig={chart.chartConfig}
        angleKey={chart.angleKey}
        dataKeys={chart.dataKeys}
      />
    );
  }

  return (
    <RadarChartView
      title={title ?? null}
      height={height ?? null}
      data={chart.chartData}
      chartConfig={chart.chartConfig}
      angleKey={chart.angleKey}
      dataKey={chart.dataKey}
    />
  );
};
